import React, { useState, useEffect } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import branding from '../../branding.json';
import { LegalDocModal } from '../common/LegalDocModal';
import {
  checkConsent,
  recordConsent,
  getRequiredConsents,
} from '../../services/consentService';

export const ConsentModal: React.FC = () => {
  const { darkMode } = useTheme();
  const { isAuthenticated, user, loading } = useAuth();
  const [visible, setVisible] = useState(false);
  const [ageConfirmed, setAgeConfirmed] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [missingConsents, setMissingConsents] = useState<string[]>([]);
  const [legalModal, setLegalModal] = useState<'terms' | 'privacy' | null>(null);

  const hasChecked = React.useRef(false);

  useEffect(() => {
    // Wait until auth is fully settled (loading=false) before checking consent.
    // This ensures the CSRF token from /api/auth/me is in localStorage before
    // the consent API call fires.
    if (loading || !isAuthenticated || !user) return;
    if (hasChecked.current) return;
    hasChecked.current = true;

    const check = async () => {
      // Brief yield to ensure the CSRF token written by /api/auth/me
      // has been flushed to localStorage before the consent check fires
      await new Promise(resolve => setTimeout(resolve, 100));
      try {
        const [consentStatus] = await Promise.all([
          checkConsent(),
          getRequiredConsents(),
        ]);
        if (consentStatus.needs_consent) {
          setMissingConsents(consentStatus.missing);
          setVisible(true);
        }
      } catch {
        // If consent check fails, don't block the user
      }
    };
    check();
  }, [loading, isAuthenticated, user]);

  const needsAge = missingConsents.some(m => m.startsWith('age_verification'));
  const needsTerms = missingConsents.some(m => m.startsWith('terms'));
  const needsPrivacy = missingConsents.some(m => m.startsWith('privacy'));

  const canSubmit =
    (!needsAge || ageConfirmed) &&
    (!needsTerms || termsAccepted) &&
    (!needsPrivacy || privacyAccepted);

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const consents = missingConsents
        .map(missing => {
          const match = missing.match(/^(.+)_v(\d+)$/);
          return match
            ? { consent_type: match[1], version: match[2] }
            : null;
        })
        .filter(Boolean) as { consent_type: string; version: string }[];

      await recordConsent(consents);
      setVisible(false);
    } catch {
      // Silently fail — user will be prompted again on next load
    }
    setSubmitting(false);
  };

  if (!visible) return null;

  const openTerms = (e: React.MouseEvent) => {
    e.preventDefault();
    setLegalModal('terms');
  };

  const openPrivacy = (e: React.MouseEvent) => {
    e.preventDefault();
    setLegalModal('privacy');
  };

  const isUpdate = missingConsents.some(m => {
    const match = m.match(/_v(\d+)$/);
    return match && parseInt(match[1]) > 1;
  });

  const primaryColor = branding.theme.primaryColor;

  return (
    <>
    {legalModal && (
      <LegalDocModal docKey={legalModal} onClose={() => setLegalModal(null)} />
    )}
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[60]">
      <div
        className={`${
          darkMode ? 'bg-gray-800 text-white' : 'bg-white text-gray-800'
        } p-8 rounded-lg shadow-xl max-w-lg w-full mx-4`}
      >
        <h2 className="text-xl font-bold mb-2">
          {isUpdate ? 'Updated Terms' : `Welcome to ${branding.appName}`}
        </h2>
        <p
          className={`text-sm mb-6 ${
            darkMode ? 'text-gray-300' : 'text-gray-600'
          }`}
        >
          {isUpdate
            ? "We've updated our legal documents. Please review and accept to continue."
            : 'Please review and accept the following to continue.'}
        </p>

        <div className="space-y-4 mb-6">
          {needsAge && (
            <label className="flex items-start space-x-3 cursor-pointer">
              <input
                type="checkbox"
                checked={ageConfirmed}
                onChange={e => setAgeConfirmed(e.target.checked)}
                className="mt-1 h-4 w-4 rounded border-gray-300 text-blue-600"
              />
              <span
                className={`text-sm ${
                  darkMode ? 'text-gray-200' : 'text-gray-700'
                }`}
              >
                I confirm that I am at least <strong>18 years of age</strong>
              </span>
            </label>
          )}

          {needsTerms && (
            <label className="flex items-start space-x-3 cursor-pointer">
              <input
                type="checkbox"
                checked={termsAccepted}
                onChange={e => setTermsAccepted(e.target.checked)}
                className="mt-1 h-4 w-4 rounded border-gray-300 text-blue-600"
              />
              <span
                className={`text-sm ${
                  darkMode ? 'text-gray-200' : 'text-gray-700'
                }`}
              >
                I agree to the{' '}
                <button
                  onClick={openTerms}
                  className="text-blue-500 hover:underline underline-offset-2"
                >
                  Terms of Service
                </button>
              </span>
            </label>
          )}

          {needsPrivacy && (
            <label className="flex items-start space-x-3 cursor-pointer">
              <input
                type="checkbox"
                checked={privacyAccepted}
                onChange={e => setPrivacyAccepted(e.target.checked)}
                className="mt-1 h-4 w-4 rounded border-gray-300 text-blue-600"
              />
              <span
                className={`text-sm ${
                  darkMode ? 'text-gray-200' : 'text-gray-700'
                }`}
              >
                I agree to the{' '}
                <button
                  onClick={openPrivacy}
                  className="text-blue-500 hover:underline underline-offset-2"
                >
                  Privacy Policy
                </button>
              </span>
            </label>
          )}
        </div>

        <button
          onClick={handleSubmit}
          disabled={!canSubmit || submitting}
          className={`w-full py-2.5 rounded-md text-white font-medium transition-all ${
            !canSubmit
              ? 'bg-gray-400 cursor-not-allowed'
              : 'hover:opacity-90'
          }`}
          style={
            canSubmit && !submitting
              ? { backgroundColor: primaryColor }
              : undefined
          }
        >
          {submitting ? 'Saving...' : 'Accept & Continue'}
        </button>
      </div>
    </div>
    </>
  );
};
